import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Activity } from 'lucide-react';
import {
  ClientMedicalInfoDto,
  medicalConditionsApi,
  getMedicalConditionStatusColor,
  handleApiError
} from '../../services/medicalConditionsService';
import { hexToRgba } from '../../utils/colorUtils';

interface DiseasesCardProps {
  clientId: number;
}

const Card = styled.div`
  padding: 24px;
  /* No box styling - unified canvas */

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TitleIcon = styled.span`
  color: var(--text-secondary);
  display: flex;
  align-items: center;
`;

const DiseasesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 -24px;

  @media (max-width: 767px) {
    margin: 0 -16px;
  }
`;

const DiseaseItem = styled.div<{ isPrimary: boolean }>`
  padding: 12px 24px;
  border-right: 3px solid ${props => props.isPrimary ? '#dc2626' : '#94a3b8'};
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 767px) {
    padding: 12px 16px;
  }
`;

const DiseaseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const DiseaseName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
`;

const TypeBadge = styled.span<{ isPrimary: boolean }>`
  background: ${props => props.isPrimary ? '#dc2626' : '#94a3b8'};
  color: white;
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 10px;
  font-weight: 600;
`;

const StatusBadge = styled.span<{ color: string }>`
  background: ${props => hexToRgba(props.color, 0.12)};
  color: ${props => props.color};
  border: 1px solid ${props => hexToRgba(props.color, 0.25)};
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 10px;
  font-weight: 600;
`;

const CategoryName = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--text-secondary);
  font-style: italic;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: rgba(251, 39, 93, 0.1);
  border: 1px solid rgba(251, 39, 93, 0.3);
  border-radius: var(--border-radius-md);
  padding: 12px;
  color: var(--error-color);
  font-size: 13px;
  text-align: right;
`;

const DiseasesCard: React.FC<DiseasesCardProps> = ({ clientId }) => {
  const [diseases, setDiseases] = useState<ClientMedicalInfoDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDiseases = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await medicalConditionsApi.getByClientId(clientId);
        setDiseases(data);
      } catch (err) {
        console.error('Error loading diseases:', err);
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };

    loadDiseases();
  }, [clientId]);

  return (
    <Card>
      <Header>
        <Title>
          <TitleIcon><Activity size={16} strokeWidth={1.5} /></TitleIcon>
          מחלות ואבחונים
        </Title>
      </Header>

      {loading && <LoadingState>טוען...</LoadingState>}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {!loading && !error && diseases.length === 0 && (
        <EmptyState>לא הוגדרו מחלות</EmptyState>
      )}

      {!loading && !error && diseases.length > 0 && (
        <DiseasesList>
          {diseases.map(disease => (
            <DiseaseItem key={disease.id} isPrimary={disease.isPrimary}>
              <DiseaseHeader>
                <DiseaseName>{disease.diseaseName}</DiseaseName>
                <BadgeGroup>
                  <TypeBadge isPrimary={disease.isPrimary}>
                    {disease.diseaseType}
                  </TypeBadge>
                  {disease.statusName && disease.status && (
                    <StatusBadge color={getMedicalConditionStatusColor(disease.status)}>
                      {disease.statusName}
                    </StatusBadge>
                  )}
                </BadgeGroup>
              </DiseaseHeader>
              <CategoryName>{disease.diseaseCategoryName}</CategoryName>
            </DiseaseItem>
          ))}
        </DiseasesList>
      )}
    </Card>
  );
};

export default DiseasesCard;
